import React, { useMemo } from "react";

import { ClipboardCheck, Download } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

import { ButtonShadcn } from "@/components/ui/Button";
import { Section, BarRow, BarsSkeleton, EmptyNote, Collapsible, LowSampleBadge, CHART, badRateColor, ExportButtons } from "./primitives";

// F5 — document & checklist quality. Data's job: rank slots by rework (→ bars,
// worst-first), show each checklist's drop-off (→ compact funnel rows), and a
// two-part self-service split (→ donut, the one place a part-of-whole donut is
// the right call besides the dashboard). Distinct lead form (donut) again.

const FUNNEL_STAGES = [
  { key: "sent", label: "Sent", color: CHART.indigo },
  { key: "uploaded", label: "Uploaded", color: CHART.blue },
  { key: "reviewed", label: "Reviewed", color: CHART.sky },
  { key: "approved", label: "Approved", color: CHART.green },
];

const PortalDonut = ({ portal, loading }) => {
  const data = useMemo(() => {
    if (!portal) return [];
    return [
      { key: "candidate", label: "Candidate / portal", value: portal.candidateUploads, color: CHART.green },
      { key: "officer", label: "Officer uploaded", value: portal.officerUploads, color: CHART.slate },
    ].filter((d) => d.value > 0);
  }, [portal]);
  const total = data.reduce((s, d) => s + d.value, 0);

  if (loading) return <BarsSkeleton rows={2} />;
  if (total === 0) return <EmptyNote>No uploads in this period.</EmptyNote>;

  return (
    <div className="flex items-center gap-4">
      <div className="relative w-[130px] h-[130px] flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="label" innerRadius={44} outerRadius={62} paddingAngle={2} stroke="none">
              {data.map((d) => (
                <Cell key={d.key} fill={d.color} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) =>
                active && payload?.length ? (
                  <div className="rounded-lg border border-border-primary bg-background-secondary px-2.5 py-1.5 shadow-lg">
                    <p className="text-[11px] font-medium text-text-primary">{payload[0].payload.label}</p>
                    <p className="text-[11px] text-text-secondary">
                      {payload[0].value} upload{payload[0].value === 1 ? "" : "s"}
                    </p>
                  </div>
                ) : null
              }
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-[1.3rem] font-bold leading-none text-text-primary">{portal?.portalUploadRate ?? 0}%</span>
          <span className="text-[9px] text-text-secondary mt-0.5">self-service</span>
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        {data.map((d) => (
          <span key={d.key} className="flex items-center gap-2 text-[11px] text-text-secondary">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: d.color }} />
            <span className="text-text-primary">{d.label}</span>
            <span className="font-semibold">{d.value}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const ChecklistFunnel = ({ row }) => {
  const max = Math.max(1, row.sent || 0);
  return (
    <div className="py-2 border-b border-border-primary last:border-b-0">
      <p className="text-[12px] text-text-primary mb-1.5 truncate">{row.checklistName}</p>
      <div className="grid grid-cols-4 gap-1.5">
        {FUNNEL_STAGES.map((s) => {
          const v = row[s.key] || 0;
          return (
            <div key={s.key}>
              <div className="h-4 rounded bg-background-tertiary overflow-hidden">
                <div className="h-full rounded transition-all duration-500" style={{ width: `${(v / max) * 100}%`, background: `${s.color}cc` }} />
              </div>
              <p className="text-[9px] text-text-secondary mt-0.5">
                {s.label} <span className="font-semibold text-text-primary">{v}</span>
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const QualitySection = ({ data, loading, onExportCsv, onExportPdf, exporting }) => {
  const slots = (data?.slotQuality ?? []).slice(0, 8);
  const funnels = (data?.checklistFunnels ?? []).slice(0, 6);
  const minSampleSize = data?.minSampleSize ?? 10;

  return (
    <Section
      icon={ClipboardCheck}
      title="Document & checklist quality"
      subtitle="Which requirements cause the most rework, and do candidates self-serve?"
      action={<ExportButtons onExportCsv={onExportCsv} onExportPdf={onExportPdf} loading={loading} busy={exporting} />}
    >
      {/* Portal engagement — the headline, expanded by default */}
      <div>
        <p className="text-[12px] font-medium text-text-primary mb-2">Candidate portal engagement</p>
        <PortalDonut portal={data?.portalEngagement} loading={loading} />
        <p className="text-[10px] text-text-secondary mt-2">Higher self-service = less officer chasing.</p>
      </div>

      {/* Detail breakdown — collapsed by default */}
      <Collapsible title="Checklist-slot rework rate & completion funnels">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Slot rework bars */}
          <div>
            <p className="text-[12px] font-medium text-text-primary mb-2">Rework rate per checklist slot (worst first)</p>
            {loading ? (
              <BarsSkeleton rows={5} />
            ) : slots.length === 0 ? (
              <EmptyNote>No documents uploaded in this period.</EmptyNote>
            ) : (
              slots.map((s) => (
                <BarRow
                  key={s.key}
                  label={s.label}
                  sublabel={`${s.rejected + s.reRequested}/${s.totalDocuments} bounced`}
                  pct={s.reworkRate}
                  valueLabel={`${s.reworkRate}%`}
                  color={s.belowMinSample ? CHART.slate : badRateColor(s.reworkRate)}
                />
              ))
            )}
          </div>

          {/* Per-checklist funnels */}
          <div>
            <p className="text-[12px] font-medium text-text-primary mb-1">Completion funnel per checklist</p>
            {loading ? (
              <BarsSkeleton rows={3} />
            ) : funnels.length === 0 ? (
              <EmptyNote>No checklists with candidates yet.</EmptyNote>
            ) : (
              <div className="flex flex-col">
                {funnels.map((f) => (
                  <ChecklistFunnel key={f.checklistId} row={f} />
                ))}
              </div>
            )}
          </div>
        </div>
      </Collapsible>
    </Section>
  );
};

export default QualitySection;
